import React, { useState, useEffect } from 'react';
import { fetchESV } from './bibleVersions/FetchESV';
import { fetchFromBibleApi } from './bibleVersions/fetchFromBibleAPI';
import './esvStyles.css'; // Import the ESV styles

interface VerseContentProps {
  verseReference: string | string[];
  version: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

interface VerseData {
  content: string;
  reference: string;
}

const VerseContent: React.FC<VerseContentProps> = ({
  verseReference,
  version,
  size = 'md',
}) => {
  const [verses, setVerses] = useState<VerseData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Helper function to format structured verse data into HTML
  const formatVersesToHTML = (verseData: {
    content: any[];
    reference: string;
  }) => {
    if (!verseData.content || verseData.content.length === 0) {
      return 'Verse content not available.';
    }

    let currentChapter = ''; // Track the current chapter
    const formattedContent = verseData.content
      .map((verse: any, index: number) => {
        const chapter = verse.chapter;
        const verseNumber = verse.verse;
        const isNewChapter = currentChapter !== chapter; // Check if the chapter has changed

        if (isNewChapter) {
          currentChapter = chapter; // Update the current chapter
        }

        // Return the formatted verse content with chapter and verse numbers
        return `
          ${
            isNewChapter
              ? `<br /><span style="font-weight: bold; font-family: serif; font-size: 24px;">${chapter}</span> `
              : ''
          }
          <span key=${verseNumber}>
            <span style="font-weight: bold; font-size: smaller; font-family: serif;">
              ${verseNumber}
            </span>
            ${verse.text}
          </span>
        `;
      })
      .join(''); // Join all verses into a single string

    return formattedContent;
  };

  // Get Tailwind classes for text sizing
  const getTextSizeClasses = (baseSize: string) => {
    const sizeMap: Record<string, Record<string, string>> = {
      xs: {
        heading: 'text-base',
        content: 'text-xs',
      },
      sm: {
        heading: 'text-lg',
        content: 'text-sm',
      },
      md: {
        heading: 'text-xl',
        content: 'text-base',
      },
      lg: {
        heading: 'text-2xl',
        content: 'text-lg',
      },
      xl: {
        heading: 'text-3xl',
        content: 'text-xl',
      },
      '2xl': {
        heading: 'text-4xl',
        content: 'text-2xl',
      },
    };
    return sizeMap[size] || sizeMap.md;
  };

  const textClasses = getTextSizeClasses(size);

  useEffect(() => {
    const fetchVerseContent = async () => {
      setLoading(true);

      if (
        !verseReference ||
        (Array.isArray(verseReference) && verseReference.length === 0)
      ) {
        setVerses([]);
        setLoading(false);
        return;
      }

      const references = Array.isArray(verseReference)
        ? verseReference
        : [verseReference];

      try {
        const fetchPromises = references.map(async (ref) => {
          let result: any;
          switch (version) {
            case 'ESV':
              result = await fetchESV(ref, {}, true);
              break;
            case 'WEB':
              result = await fetchFromBibleApi(ref, 'WEB');
              break;
            case 'KJV':
              result = await fetchFromBibleApi(ref, 'KJV');
              break;
            case 'ASV':
              result = await fetchFromBibleApi(ref, 'ASV');
              break;
            default:
              result = await fetchFromBibleApi(ref, 'WEB');
          }

          // Handle different result formats
          if (
            typeof result === 'object' &&
            result !== null &&
            result.content &&
            Array.isArray(result.content)
          ) {
            // New structured format from fetchFromBibleApi (WEB, KJV, ASV)
            return {
              content: formatVersesToHTML(result),
              reference: result.reference || ref,
            };
          } else if (typeof result === 'object' && result !== null) {
            // Old object format (ESV with content property)
            return {
              content: result.content || 'Verse not available.',
              reference: result.reference || ref,
            };
          } else {
            // String format (old ESV)
            return {
              content: result || 'Verse not available.',
              reference: ref,
            };
          }
        });

        const results = await Promise.all(fetchPromises);
        setVerses(results);
      } catch (error) {
        console.error('Error fetching verse content:', error);
        setVerses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVerseContent();
  }, [verseReference, version]);

  if (loading) {
    return (
      <div className='flex flex-col items-center'>
        <p
          className={`text-center ${textClasses.content} text-gray-500 italic mb-6`}
        >
          Loading verses...
        </p>
      </div>
    );
  }

  if (verses.length === 0) {
    return (
      <div className='flex flex-col items-center'>
        <p
          className={`text-center ${textClasses.content} text-gray-500 italic mb-6`}
        >
          No verses available.
        </p>
      </div>
    );
  }

  return (
    <div className='flex flex-col items-center space-y-6'>
      {verses.map((verse, index) => (
        <div key={index} className='flex flex-col items-center'>
          <h2 className={`${textClasses.heading} font-bold text-gray-800 mb-4`}>
            {verse.reference}
          </h2>
          <div
            dangerouslySetInnerHTML={{ __html: verse.content }}
            className={`${textClasses.content} ${
              version === 'ESV' ? 'esv-text' : ''
            }`}
          />
        </div>
      ))}
    </div>
  );
};

export default VerseContent;
